import { Button, type ComboboxItem, Modal, Select } from "@mantine/core";
import { useCallback, useState } from "react";
import type { ApiError } from "../../../../../api";
import { ModalFooter } from "../../../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../../../mantine/notifications/useToast";
import { getFieldSpecificError } from "../../../../../utils/api-utils";
import { getErrorText } from "../../../../../utils/get-error-text";
import type {
  EnergyDataImportRequest,
  EnergyDataImportRequestDetails
} from "../../../EnergyDataUploadFlow/EnergyDataUpload";
import { retryUploadWithParameterSetRequest } from "../../../EnergyDataUploadFlow/EnergyDataUpload";
import { GeneralImportDetailsList } from "../../EnergyDataImportDetailsModal/GeneralImportDetailsList";
import { EnergyDataImportDetailsTable } from "../../EnergyDataImportDetailsTable/EnergyDataImportDetailsTable";
import { useDataImportRequestDetails } from "../../hooks/useDataImportRequestDetails";

interface Props {
  dataImportRequest: EnergyDataImportRequest;
  onClose: () => void;
  onRetryUploadDone: (dataImportRequest: EnergyDataImportRequest) => void;
}

function ParameterSetModal({
  dataImportRequest,
  onClose,
  onRetryUploadDone
}: Props) {
  const { showErrorToast } = useToast();
  const { dataImportRequestWithDetails, isLoading, error } =
    useDataImportRequestDetails(dataImportRequest.id);

  const [parameterSetId, setParameterSetId] = useState<string | null>(null);
  const [matchedDetails, setMatchedDetails] = useState<
    EnergyDataImportRequestDetails[]
  >([]);

  const parameterSets = dataImportRequestWithDetails?.details.reduce<
    Array<ComboboxItem>
  >((acc, { parameterSet: { id, name, originName } = {} }) => {
    if (acc.find(({ value }) => value === id)) {
      return acc;
    }

    return [
      ...acc,
      {
        value: id || "",
        label: originName ? `${originName}: ${name}` : name || ""
      }
    ];
  }, []);

  const onSelectParameterSet = useCallback(
    (value: string) => {
      const selectedParameterSet = value ?? null;
      setParameterSetId(selectedParameterSet);

      setMatchedDetails(
        dataImportRequestWithDetails?.details.filter(
          ({ parameterSet: { id } = {} }) => id === selectedParameterSet
        )
      );
    },
    [dataImportRequestWithDetails?.details]
  );

  const handleRetryUpload = useCallback(() => {
    retryUploadWithParameterSetRequest(dataImportRequest.id, parameterSetId)
      .then((response) => {
        onRetryUploadDone(response.data);
      })
      .catch((e: ApiError) => {
        const errorMessage = getFieldSpecificError(e, ["parameterSetId"]);
        const errorText = getErrorText(
          e,
          errorMessage ? { [e.response?.status as number]: errorMessage } : {}
        );
        showErrorToast(errorText);
      })
      .finally(() => {
        onClose();
      });
  }, [
    dataImportRequest.id,
    onClose,
    onRetryUploadDone,
    parameterSetId,
    showErrorToast
  ]);

  return (
    <Modal
      className="ParameterSetModal"
      opened
      size="min(80vw, 1200px)"
      title={`Importdetails: ${dataImportRequest.energyDataFile.name}`}
      onClose={onClose}
    >
      <GeneralImportDetailsList
        generalDetails={
          isLoading ? dataImportRequest : dataImportRequestWithDetails
        }
      />
      <p>
        Wir haben mehrere Dateiformate erkannt, mit denen die Datei importiert
        werden könnte. Bitte wählen Sie das passende Format aus und klicken Sie
        auf Wiederholen. Die Werte aus der Tabelle helfen Ihnen dabei das
        passende Format auszuwählen.
      </p>
      <Select
        data={parameterSets}
        mb="sm"
        miw={150}
        required
        scrollAreaProps={{
          style: { wordBreak: "break-all" }
        }}
        onChange={(newValue: string | null) => {
          if (newValue !== null) {
            onSelectParameterSet(newValue);
          }
        }}
      />
      <EnergyDataImportDetailsTable
        customNoDataMessage="Bitte wählen Sie das passende Format"
        dataImportRequestDetails={matchedDetails}
        dataType={dataImportRequest.typeOfData}
        error={error}
        isLoading={isLoading}
      />
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          color="secondary"
          disabled={parameterSetId === null}
          onClick={handleRetryUpload}
        >
          Wiederholen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export { ParameterSetModal };
